// Statements (ADR-0037): the financial statements rebuilt from the general ledger, the journal entries and the trial balance, beside the
// client's own figures — the statement of financial position, the statement of activities, the statement of cash flows and the
// statement of functional expenses — with where every account goes and the account flow (entry kinds → statement sections →
// statements, drawn by the explainer's flow component). Every line opens to its accounts and each account to its ledger rows.
// Everything shown is the engine's (planning key "statements" = prelim.statements); the page computes no figure. It follows the
// engine live (ADR-0032).
import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { Card, EmptyState, ErrorState, LoadingRows, StatusPill, TabPanel, Tabs } from "../../components";
import { useLiveTables } from "../../data/live";
import { useLoad } from "../../lib/hooks";
import { KIND_LABEL, SECTION_LABEL, amt, diffState, statementsDoc, type StAccount, type StClient, type StEntry, type Statements } from "../../lib/statements";
import { fmtStamp } from "../../lib/format";
import { useWs } from "./common";
import { FlowDiagram } from "./IssueExplainer";
import { LiveStamp } from "./LiveStamp";

const TABS = [["position", "Financial position"], ["activities", "Activities"], ["cashflows", "Cash flows"], ["functional", "Functional expenses"],
  ["map", "Where accounts go"], ["flow", "Account flow"], ["differences", "Differences"]] as const;
type Tab = typeof TABS[number][0];

export function WsStatements() {
  const ws = useWs();
  const { ds } = useApp();
  const docs = useLoad(() => ds.table(ws.id, "planning"), [ds, ws.id]);
  const live = useLiveTables(ds, ws.id, ["planning"], docs.reload);
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some(([id]) => id === params.get("tab")) ? params.get("tab") : "position") as Tab;
  const setTab = (t: string) => { const p = new URLSearchParams(params); if (t === "position") p.delete("tab"); else p.set("tab", t); setParams(p, { replace: true }); };
  const head = (
    <header className="flex flex-wrap items-end justify-between gap-2">
      <div><h1 className="text-xl font-semibold">Statements</h1>
        <p className="text-sm text-muted">The financial statements rebuilt from the general ledger, journal entries and trial balance, against the client's.</p></div>
      <LiveStamp live={live} />
    </header>
  );
  if (docs.error) return <div className="space-y-4">{head}<ErrorState error={docs.error} onRetry={docs.reload} /></div>;
  if (!docs.data) return <div className="space-y-4">{head}<LoadingRows rows={8} label="Loading the statements" /></div>;
  const { doc, error } = statementsDoc(docs.data);
  if (error) return <div className="space-y-4">{head}<ErrorState error={`The engine could not publish the rebuild: ${error}`} onRetry={docs.reload} /></div>;
  if (!doc) return <div className="space-y-4">{head}<EmptyState title="The engine has not published the rebuild yet.">It appears after the engine's next publish of this engagement (prelim.statements).</EmptyState></div>;
  if (!doc.available) return <div className="space-y-4">{head}<EmptyState title="Nothing to rebuild yet.">{doc.why}</EmptyState></div>;
  const bad = doc.checks.filter((c) => !c.agrees);
  const counts: Record<Tab, number | undefined> = {
    position: undefined, activities: undefined, cashflows: undefined, functional: doc.functional?.investigation.length, map: doc.conflicts.length + doc.unexplained.length || undefined,
    flow: undefined, differences: doc.compare.filter((x) => diffState(x.difference) === "differs").length,
  };
  return (
    <div className="space-y-4">
      {head}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatusPill kind={bad.length ? "blocked" : "done"} label={bad.length ? `${bad.length} control total${bad.length === 1 ? "" : "s"} do not hold` : `All ${doc.checks.length} control totals hold`}
          title={doc.checks.map((c) => `${c.agrees ? "✓" : "✗"} ${c.check}`).join("\n")} />
        {doc.latest_run ? <span className="text-muted">Filed with preliminary run {doc.latest_run.run} ({fmtStamp(doc.latest_run.at)}, {doc.latest_run.by}):{" "}
          {doc.latest_run.files.filter((f) => f.endsWith(".xlsx")).map((f) => f.split("/").pop()).join(" · ")}</span>
          : <span className="text-muted">Not filed yet — run the preliminary analysis (prelim.run).</span>}
        <Link className="text-brand hover:underline" to={`/e/${ws.id}/folders/02/${encodeURIComponent("02.00 Preliminary Analysis")}`}>02.00 Preliminary Analysis</Link>
      </div>
      {bad.length > 0 && (
        <ul role="alert" className="rounded-md border border-blocked-line bg-blocked-bg px-3 py-2 text-sm">
          {bad.map((c) => <li key={c.check}>{c.check}: {amt(c.value)} against {amt(c.expected)}</li>)}
        </ul>
      )}
      <Tabs label="Statement" value={tab} onChange={setTab} idPrefix="st" tabs={TABS.map(([id, label]) => ({ id, label, count: counts[id] }))} />
      <TabPanel id="position" value={tab} idPrefix="st"><Position doc={doc} /></TabPanel>
      <TabPanel id="activities" value={tab} idPrefix="st"><Activities doc={doc} /></TabPanel>
      <TabPanel id="cashflows" value={tab} idPrefix="st"><CashFlows doc={doc} /></TabPanel>
      <TabPanel id="functional" value={tab} idPrefix="st"><Functional doc={doc} /></TabPanel>
      <TabPanel id="map" value={tab} idPrefix="st"><AccountMap doc={doc} /></TabPanel>
      <TabPanel id="flow" value={tab} idPrefix="st"><Flow doc={doc} /></TabPanel>
      <TabPanel id="differences" value={tab} idPrefix="st"><Differences doc={doc} /></TabPanel>
      {doc.note && <p className="text-xs text-muted">{doc.note}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── shared pieces

const num = "px-3 py-1.5 text-right font-mono tabular-nums";
const DIFF_TONE = { agrees: "text-done", rounding: "text-waiting", differs: "text-blocked font-semibold", none: "text-muted" } as const;

function Diff({ c, label }: { c: StClient | null; label: string }) {
  if (!c) return <td className={num} />;
  const s = diffState(c.difference);
  const words = s === "agrees" ? "agrees" : s === "rounding" ? "rounding" : s === "differs" ? "differs" : "";
  return (
    <td className={`${num} ${DIFF_TONE[s]}`} title={c.candidates.join("; ")} aria-label={`${label}: client ${amt(c.client)}, difference ${amt(c.difference)}${words ? `, ${words}` : ""}`}>
      {amt(c.client)}{c.difference ? <span className="ml-1 text-[11px]">({amt(c.difference)})</span> : null}
    </td>
  );
}

/** A statement line that opens to its accounts, each of which opens to its ledger rows. */
function LineRow({ caption, cells, accounts, doc, depth = 1 }: { caption: string; cells: ReactNode; accounts: string[]; doc: Statements; depth?: number }) {
  const [open, setOpen] = useState(false);
  const has = accounts.length > 0;
  return (
    <>
      <tr className="align-top hover:bg-sunken/50">
        <td className="px-3 py-1.5" style={{ paddingLeft: `${depth * 0.75}rem` }}>
          {has ? <button type="button" className="text-left hover:underline" aria-expanded={open} onClick={() => setOpen(!open)}
            aria-label={`${caption}: ${open ? "hide" : "show"} its ${accounts.length} account${accounts.length === 1 ? "" : "s"}`}>
            <span aria-hidden="true" className="mr-1 inline-block w-3 text-muted">{open ? "▾" : "▸"}</span>{caption}</button> : caption}
        </td>
        {cells}
      </tr>
      {open && (
        <tr><td colSpan={8} className="bg-sunken/40 px-3 py-2"><Accounts codes={accounts} doc={doc} /></td></tr>
      )}
    </>
  );
}

function Accounts({ codes, doc }: { codes: string[]; doc: Statements }) {
  const rows = codes.map((c) => doc.accounts.find((a) => a.code === c)).filter((a): a is StAccount => !!a);
  return (
    <ul className="space-y-1 text-xs" aria-label="Accounts on the line">
      {rows.map((a) => <AccountItem key={a.code} a={a} doc={doc} />)}
    </ul>
  );
}

function AccountItem({ a, doc }: { a: StAccount; doc: Statements }) {
  return (
    <li id={`acct-${a.code}`}>
      <details>
        <summary className="cursor-pointer">
          <span className="font-mono">{a.code}</span> {a.name} · current year <span className="font-mono">{amt(a.cy)}</span> · prior year <span className="font-mono">{amt(a.py)}</span>
          {a.kind && <span className="text-muted"> · {KIND_LABEL[a.kind] ?? a.kind}</span>}
          {a.roll && a.roll.difference !== 0 && <span className="text-blocked"> · does not roll forward ({amt(a.roll.difference)})</span>}
        </summary>
        <div className="mt-1 space-y-1 pl-4">
          {a.basis && <p className="text-muted">Why there: {a.basis}{a.fs_line ? ` · engagement mapping: ${a.fs_line} (${a.area})` : ""}</p>}
          {a.roll && <p>Opening {amt(a.roll.opening)} + the year's entries {amt(a.roll.activity)} = {amt(a.roll.closing)}; trial balance {amt(a.roll.tb)}.</p>}
          {a.gl.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-[560px] text-xs"><caption className="sr-only">Ledger rows of {a.code}</caption>
                <thead className="text-left text-muted"><tr><th className="pr-3 font-medium">GL row</th><th className="pr-3 font-medium">Date</th><th className="pr-3 font-medium">Kind</th>
                  <th className="pr-3 font-medium">Reference</th><th className="pr-3 font-medium">Description</th><th className="text-right font-medium">Amount</th></tr></thead>
                <tbody>{a.gl.map((g) => (
                  <tr key={g.row} id={`gl-${g.row}`}><td className="pr-3 font-mono">{g.row}</td><td className="pr-3">{g.date}</td><td className="pr-3">{g.type}</td><td className="pr-3">{g.reference}</td>
                    <td className="pr-3">{g.memo}</td><td className="text-right font-mono tabular-nums">{amt(g.amount)}</td></tr>))}</tbody>
              </table>
              {a.gl_rows > a.gl.length && <p className="text-muted">The {a.gl.length} largest of {a.gl_rows} ledger rows are shown; every row is on the GL sheet of the FS workbook.</p>}
            </div>
          ) : <p className="text-muted">{doc.ledger.imported ? "No ledger rows this year." : "No general ledger imported."}</p>}
        </div>
      </details>
    </li>
  );
}

function Table({ caption, head, children }: { caption: string; head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-raised">
      <table className="w-full min-w-[640px] text-sm"><caption className="sr-only">{caption}</caption>
        <thead className="border-b border-line bg-sunken text-left text-xs text-muted"><tr>{head.map((h, i) => <th key={h + i} className={`px-3 py-1.5 font-medium ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}

const Total = ({ label, values, strong = true }: { label: string; values: Array<number | null | undefined>; strong?: boolean }) => (
  <tr className={strong ? "border-t-2 border-ink/30 font-semibold" : "text-muted"}><td className="px-3 py-1.5">{label}</td>{values.map((v, i) => <td key={i} className={num}>{v === undefined ? "" : amt(v)}</td>)}</tr>
);

// ─────────────────────────────────────────────────────────────── the statements

function Position({ doc }: { doc: Statements }) {
  const P = doc.position;
  const secs = ["assets", "liabilities", "na_without", "na_with"];
  const tot: Record<string, string> = { assets: "total_assets", liabilities: "total_liabilities", na_without: "na_without", na_with: "na_with" };
  const ct = P.client_totals ?? { cy: {}, py: {} };
  const TotalRow = ({ label, k, ck }: { label: string; k: string; ck: string }) => (
    <tr className="border-t-2 border-ink/30 font-semibold"><td className="px-3 py-1.5">{label}</td><td className={num}>{amt(P.cy[k])}</td><Diff c={ct.cy[ck] ?? null} label={`${label}, client`} />
      <td className={num}>{amt(P.py[k])}</td><Diff c={ct.py[ck] ?? null} label={`${label}, prior-year audited`} /></tr>
  );
  return (
    <section id="ex-fs-position" aria-label="Statement of financial position" className="space-y-2">
      <Table caption="Statement of financial position, rebuilt" head={["", "Current year (rebuilt)", "Client's draft", "Prior year (rebuilt)", "Prior-year audited"]}>
        {secs.map((s) => (
          <SectionRows key={s} label={SECTION_LABEL[s]}>
            {P.lines.filter((x) => x.section === s).map((x) => (
              <LineRow key={x.caption} caption={x.caption} accounts={x.accounts} doc={doc}
                cells={<><td className={num}>{amt(x.cy)}</td><Diff c={x.client_cy} label="Client" /><td className={num}>{amt(x.py)}</td><Diff c={x.client_py} label="Prior-year audited" /></>} />
            ))}
            {s.startsWith("na_") && P.result_unclosed && <Total strong={false} label="   Change in net assets for the year (before closing)" values={[P.result_unclosed[s.slice(3) as "without" | "with"], undefined, undefined, undefined]} />}
            <TotalRow label={`Total ${SECTION_LABEL[s].toLowerCase()}`} k={tot[s]} ck={s} />
          </SectionRows>
        ))}
        <TotalRow label="Total net assets" k="total_net_assets" ck="total_net_assets" />
        <TotalRow label="Total liabilities and net assets" k="total_liabilities_and_net_assets" ck="total_liabilities_and_net_assets" />
      </Table>
      <p className="text-xs text-muted">The client's columns show the client's figure, with the difference from the rebuild in brackets when there is one (a line of another year's
        statement is matched by its accounts); hover for the explanation candidates.</p>
    </section>
  );
}

function SectionRows({ label, children }: { label: string; children: ReactNode }) {
  return <><tr className="bg-sunken/60"><th colSpan={8} scope="rowgroup" className="px-3 py-1 text-left text-xs font-semibold uppercase text-muted">{label}</th></tr>{children}</>;
}

function Activities({ doc }: { doc: Statements }) {
  const S = doc.activities;
  return (
    <section id="ex-fs-activities" aria-label="Statement of activities" className="space-y-2">
      <Table caption="Statement of activities, rebuilt" head={["", "Without donor restrictions", "With donor restrictions", "Total", "Client's draft"]}>
        {(["revenue", "releases", "expenses"] as const).map((s) => {
          const ls = S.lines.filter((x) => x.section === s);
          if (!ls.length) return null;
          return (
            <SectionRows key={s} label={SECTION_LABEL[s]}>
              {ls.map((x) => <LineRow key={x.caption} caption={x.caption} accounts={x.accounts} doc={doc}
                cells={<><td className={num}>{amt(x.without)}</td><td className={num}>{amt(x.with)}</td><td className={num}>{amt(x.total)}</td><Diff c={x.client} label="Client" /></>} />)}
            </SectionRows>
          );
        })}
        <Total label="Total revenue, support and gains" values={[S.revenue.without, S.revenue.with, S.revenue.total, undefined]} />
        <Total label="Total expenses" values={[S.expenses, 0, S.expenses, undefined]} />
        <Total label="Change in net assets" values={[S.change.without, S.change.with, S.change.total, undefined]} />
        <Total strong={false} label="Net assets, beginning of year (current-year books)" values={[S.begin.without, S.begin.with, S.begin.total, undefined]} />
        <Total strong={false} label="Changes not through activities (posted directly)" values={[S.other.without, S.other.with, S.other.total, undefined]} />
        <Total label="Net assets, end of year" values={[S.ending.without, S.ending.with, S.ending.total, undefined]} />
        {S.py_end && <Total strong={false} label="Prior-year closing (prior-year trial balance)" values={[S.py_end.without, S.py_end.with, S.py_end.total, undefined]} />}
      </Table>
    </section>
  );
}

function CashFlows({ doc }: { doc: Statements }) {
  const C = doc.cashflows;
  const part = (label: string, ls: typeof C.operating, sum: number, key: string) => (
    <SectionRows key={key} label={label}>
      {ls.map((x) => <LineRow key={x.key} caption={x.caption} accounts={x.accounts} doc={doc} cells={<td className={num}>{amt(x.amount)}</td>} />)}
      <Total label={`Net cash from ${key} activities`} values={[sum]} />
    </SectionRows>
  );
  return (
    <section id="ex-fs-cashflows" aria-label="Statement of cash flows" className="space-y-3">
      <Table caption="Statement of cash flows, rebuilt (indirect method)" head={["", "Amount"]}>
        {part("Operating activities", C.operating, C.sums.operating, "operating")}
        {part("Investing activities", C.investing, C.sums.investing, "investing")}
        {part("Financing activities", C.financing, C.sums.financing, "financing")}
        <Total strong={false} label="Changes in net assets not through activities" values={[C.other_na]} />
        <Total label="Net change in cash" values={[C.net_change]} />
        <Total strong={false} label="Cash, beginning of year" values={[C.cash_beginning]} />
        <Total label="Cash, end of year" values={[C.cash_ending]} />
        <Total strong={false} label="Cash on the statement of financial position" values={[C.sfp_cash]} />
      </Table>
      {C.gl_cash_activity !== null && (
        <Card title="Cross-check to the general ledger">
          <p className="text-sm">The ledger's cash lines add to <span className="font-mono">{amt(C.gl_cash_activity)}</span>; the trial balances change cash by <span className="font-mono">{amt(C.net_change)}</span>
            {C.gl_cash_activity === C.net_change ? " — they agree." : <span className="text-blocked"> — they differ by {amt(C.gl_cash_activity - C.net_change)}.</span>}</p>
          {C.gl_sections && <p className="mt-1 text-xs text-muted">By the category of each entry's other lines: {Object.entries(C.gl_sections).map(([k, v]) => `${k.replace(/_/g, " ")} ${amt(v)}`).join(" · ")}</p>}
        </Card>
      )}
      <Card title="What the trial balance cannot give"><ul className="list-disc space-y-0.5 pl-5 text-sm">{C.cannot.map((t) => <li key={t}>{t}</li>)}</ul></Card>
    </section>
  );
}

function Functional({ doc }: { doc: Statements }) {
  const X = doc.functional;
  if (!X) return <EmptyState title="No functional expense analysis.">It is built by the preliminary run.</EmptyState>;
  const label = (c: string) => X.col_labels[c] ?? c;
  return (
    <section aria-label="Statement of functional expenses" className="space-y-3">
      <p className="text-sm">{X.path === "expectation"
        ? "The client has not provided the year's statement: this is the auditor's expectation — the trial balance by line, allocated on the prior-year audited basis."
        : "The client's statement against the trial balance and against the expectation on the prior-year basis."}
        {X.pm !== null && <span className="text-muted"> Performance materiality {amt(X.pm)}.</span>}</p>
      <Table caption="Statement of functional expenses" head={["", ...X.cols.map(label), "Total", "Prior year", ...(X.path === "comparison" ? ["Client total", "Client − TB"] : [])]}>
        {X.lines.map((x) => (
          <tr key={x.caption} title={x.basis}>
            <td className="px-3 py-1.5">{x.caption}{x.kind === "default" && x.total ? <span className="ml-1 text-xs text-waiting">(not allocated)</span> : null}</td>
            {X.cols.map((c) => <td key={c} className={num}>{amt(x.expectation[c])}</td>)}
            <td className={`${num} font-semibold`}>{amt(x.total)}</td><td className={num}>{amt(x.py_total)}</td>
            {X.path === "comparison" && <><td className={num}>{amt(x.client_total)}</td><td className={`${num} ${DIFF_TONE[diffState(x.client_vs_tb)]}`}>{amt(x.client_vs_tb)}</td></>}
          </tr>
        ))}
      </Table>
      {X.py && (X.py.cross.length > 0 || X.py.grand_diff || X.py.tie.some((t) => t.note)) && (
        <Card title="The prior-year audited statement">
          <ul className="list-disc space-y-0.5 pl-5 text-sm">
            {X.py.cross.map((c) => <li key={c.caption}>{c.caption}: printed total {amt(c.printed)}, its functions add to {amt(c.sum)} (rounding).</li>)}
            {X.py.grand_diff ? <li>The printed line totals add to {amt(X.py.line_total_sum)}, {amt(Math.abs(X.py.grand_diff))} {X.py.grand_diff > 0 ? "above" : "below"} the printed grand total {amt(X.py.grand)}.</li> : null}
            {X.py.tie.filter((t) => t.note && t.note !== "rounding").map((t) => <li key={t.caption}>{t.caption}: statement {amt(t.statement)}, prior-year trial balance {amt(t.tb)} — {t.note}.</li>)}
          </ul>
        </Card>
      )}
      <Card title={`To investigate · ${X.investigation.length}`}>
        {X.investigation.length === 0 ? <p className="text-sm text-muted">Nothing raised.</p> : (
          <ol className="list-decimal space-y-1 pl-5 text-sm">{X.investigation.map((i) => <li key={i.n}><span className="font-medium">{i.matter}.</span> {i.test} <span className="text-xs text-muted">({i.where})</span></li>)}</ol>
        )}
      </Card>
    </section>
  );
}

function AccountMap({ doc }: { doc: Statements }) {
  return (
    <section aria-label="Where the accounts go" className="space-y-3">
      {doc.conflicts.length > 0 && (
        <Card title={`Conflicts · ${doc.conflicts.length} (shown, never overridden)`} className="border-blocked-line">
          <ul className="list-disc space-y-0.5 pl-5 text-sm">{doc.conflicts.map((c, i) => <li key={i}>{c.text}.</li>)}</ul>
        </Card>
      )}
      <Table caption="Where every trial balance account goes" head={["Account", "Current year", "Line", "How found", "Engagement mapping"]}>
        {doc.accounts.map((a) => (
          <tr key={a.code} className="align-top">
            <td className="px-3 py-1.5"><span className="font-mono text-xs">{a.code}</span> {a.name}</td>
            <td className={num}>{amt(a.cy)}</td>
            <td className="px-3 py-1.5 text-right">{a.position ?? a.activities}</td>
            <td className={`px-3 py-1.5 text-right text-xs ${a.kind === "unexplained" || a.kind === "residual" ? "text-waiting" : "text-muted"}`} title={a.basis ?? undefined}>{KIND_LABEL[a.kind ?? ""] ?? a.kind}</td>
            <td className="px-3 py-1.5 text-right text-xs text-muted">{a.fs_line ? `${a.fs_line} (${a.area})` : "not mapped"}</td>
          </tr>
        ))}
      </Table>
    </section>
  );
}

function Entries({ title, items }: { title: string; items: StEntry[] }) {
  return (
    <Card title={`${title} · ${items.length}`}>
      {items.length === 0 ? <p className="text-sm text-muted">None.</p> : (
        <ul className="space-y-0.5 text-sm">{items.map((x) => <li key={x.entry}>{x.date} {x.reference || x.type} — {x.memo} · net assets <span className="font-mono">{amt(x.net_assets)}</span> · accounts {x.accounts.join(", ")} · GL rows {x.rows.join(", ")}</li>)}</ul>
      )}
    </Card>
  );
}

const SEC_KEY: Record<string, string> = { Cash: "cash", "Other assets": "assets", Liabilities: "liabilities", "Net assets": "na", Revenue: "revenue", Expenses: "expense" };

function Flow({ doc }: { doc: Statements }) {
  const L = doc.ledger;
  return (
    <section aria-label="Account flow" className="space-y-3">
      {doc.how.nodes.length > 0 && <FlowDiagram how={{ ...doc.how, break: { ...doc.how.break } }} />}
      <p className={`rounded-md border px-3 py-2 text-sm ${doc.how.break.node ? "border-blocked-line bg-blocked-bg" : "border-line"}`} role="note">{doc.how.break.text}</p>
      {!L.imported ? <EmptyState title="No general ledger imported.">The flows cannot be derived until the ledger is imported (a request was drafted).</EmptyState> : (
        <>
          <Table caption="Kinds of entry" head={["Kind of entry", "Entries", "Lines", "Debits"]}>
            {L.types.map((t) => <tr key={t.type} id={`ex-fs-type-${t.type}`}><td className="px-3 py-1.5">{t.label}</td><td className={num}>{t.entries}</td><td className={num}>{t.lines}</td><td className={num}>{amt(t.debits)}</td></tr>)}
          </Table>
          <Table caption="Flows between statement sections" head={["From (credited)", "To (debited)", "Amount"]}>
            {L.matrix.map((m, i) => <tr key={`${m.from}>${m.to}`} id={L.matrix.findIndex((y) => y.from === m.from) === i ? `ex-fs-sec-${SEC_KEY[m.from] ?? m.from}` : undefined}><td className="px-3 py-1.5">{m.from}</td><td className="px-3 py-1.5 text-right">{m.to}</td><td className={num}>{amt(m.amount)}</td></tr>)}
          </Table>
          <Entries title="Entries posted directly to net assets" items={L.to_equity} />
          <Entries title="Entries that change net assets without going through activities" items={L.bypass} />
          <Entries title="Transfers between net asset classes" items={L.na_transfers} />
        </>
      )}
    </section>
  );
}

function Differences({ doc }: { doc: Statements }) {
  const rows = doc.compare.filter((x) => x.difference !== null && x.difference !== 0);
  return (
    <section aria-label="Differences from the client's statements" className="space-y-3">
      {rows.length === 0 ? <EmptyState title="No differences.">Every client line that could be rebuilt agrees.</EmptyState> : (
        <Table caption="Client lines that differ from the rebuild" head={["Line", "Year", "Client", "Rebuilt", "Difference", "Explanation candidates"]}>
          {rows.map((x, i) => (
            <tr key={i} className="align-top">
              <td className="px-3 py-1.5">{x.caption}<div className="text-xs text-muted">{x.statement === "position" ? "Financial position" : "Activities"} · {x.locator}</div></td>
              <td className="px-3 py-1.5 text-right">{x.period === "cy" ? "Current" : "Prior (audited)"}</td>
              <td className={num}>{amt(x.client)}</td><td className={num}>{amt(x.rebuilt)}</td>
              <td className={`${num} ${DIFF_TONE[diffState(x.difference)]}`}>{amt(x.difference)}</td>
              <td className="px-3 py-1.5 text-right text-xs">{x.candidates.join("; ")}</td>
            </tr>
          ))}
        </Table>
      )}
      <Card title={`Findings of the rebuild · ${doc.findings.length}`}>
        <ul className="space-y-1 text-sm">{doc.findings.map((f, i) => (
          <li key={i}><StatusPill kind={f.severity === "exception" ? "blocked" : f.severity === "attention" ? "waiting" : "neutral"} label={f.severity === "exception" ? "Exception" : f.severity === "attention" ? "For attention" : "Information"} />{" "}
            <span className="font-medium">{f.test}.</span> {f.text}</li>))}</ul>
        <p className="mt-2 text-xs text-muted">Cite them from the preliminary run's findings (prelim:&lt;run&gt;.&lt;n&gt;).</p>
      </Card>
    </section>
  );
}
